import React, { useState, useEffect } from 'react';
import { Shield, CheckCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext.jsx';
import { isCrawlerUA } from '../utils/crawlerDetection';

const AgeVerification = ({ children }) => {
  const [isVerified, setIsVerified] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { t } = useLanguage();

  useEffect(() => {
    // Check if user has already verified
    const verified = localStorage.getItem('rcm_age_verified');
    if (verified === 'true') {
      setIsVerified(true);
    }
    // Skip age verification for admin page
    if (window.location.pathname.startsWith('/admin')) {
      setIsVerified(true);
    }
    // Skip age verification for search engine + AI crawler bots so they
    // index the real product content instead of an "Age Verification
    // Required" modal (which Google classifies as a Soft 404). Bots cannot
    // click "Confirm" — without this exemption, Googlebot's JS-rendered
    // view shows the modal overlay as the dominant element and the page
    // appears to gate all content behind a required interaction.
    // This is NOT cloaking — bots receive the same final content humans
    // see after one click.
    if (typeof navigator !== 'undefined' && isCrawlerUA(navigator.userAgent)) {
      setIsVerified(true);
    }
    setIsLoading(false);
  }, []);

  const handleVerify = () => {
    localStorage.setItem('rcm_age_verified', 'true');
    setIsVerified(true);
    // Notify the promotion announcement bar (and anything else that
    // cares) that the age gate just closed so it can recalculate its
    // visibility/position. Without this, the promo bar was rendered
    // BEHIND the age-modal overlay backdrop on first paint and never
    // recovered on desktop, matching the 2026-07-30 "banner in HTML
    // but not visible" report.
    try {
      window.dispatchEvent(new CustomEvent('rcm:age-verified'));
    } catch { /* older browsers */ }
    // Stay on current page after verification (don't redirect)
  };

  const handleDecline = () => {
    window.location.href = 'https://www.google.com';
  };

  if (isLoading) {
    return null;
  }

  if (isVerified) {
    return children;
  }

  // Show both the page content and the modal overlay
  return (
    <>
      {/* Render children in background */}
      <div style={{ filter: 'blur(2px)', pointerEvents: 'none' }}>
        {children}
      </div>
      
      {/* Age verification modal */}
      <div 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.3)',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'flex-end',
          zIndex: 9999,
          padding: '20px'
        }}
      >
      <div 
        style={{
          background: 'var(--bg-secondary)',
          maxWidth: '420px',
          width: '100%',
          borderRadius: '12px',
          overflow: 'hidden',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.4)',
          position: 'relative'
        }}
      >
        {/* Header Section */}
        <div 
          style={{
            background: 'linear-gradient(135deg, #00E5FF 0%, #0A1F44 100%)',
            padding: '24px 30px 20px',
            textAlign: 'center'
          }}
        >
          {/* Brand mark — sits in the shared white pill container so the
           * dark-navy RCM logo reads against the teal→navy gradient. */}
          <div
            className="rcm-logo-container"
            data-testid="age-modal-logo-container"
            style={{ margin: '0 auto 14px', padding: '6px 10px' }}
          >
            <img
              src="/rcm-logo-2026.webp"
              alt="RCM Biosciences"
              width="62" height="46" decoding="async"
              style={{ height: '46px', width: 'auto' }}
            />
          </div>
          <div 
            style={{
              background: 'var(--bg-secondary)',
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 12px',
              boxShadow: '0 4px 14px rgba(0,0,0,0.15)'
            }}
          >
            <Shield size={30} style={{ color: '#00E5FF' }} />
          </div>
          
          <h2 style={{ 
            fontSize: '20px', 
            fontWeight: '600', 
            color: '#ffffff',
            marginBottom: '4px'
          }}>
            {t('ageVerification.title')}
          </h2>
          
          <p style={{ 
            fontSize: '12px', 
            color: 'rgba(255,255,255,0.8)',
            margin: 0
          }}>
            RCM Biosciences
          </p>
        </div>

        {/* Content Section */}
        <div style={{ padding: '20px 24px 24px' }}>
          <p style={{ 
            fontSize: '13px', 
            color: '#C0C7D1',
            textAlign: 'center',
            marginBottom: '16px',
            lineHeight: '1.5'
          }}>
            By entering, you confirm that:
          </p>

          <div style={{ marginBottom: '16px' }}>
            {[
              { text: 'You are at least 18 years of age' },
              { text: 'You are a qualified researcher or authorized entity' },
              { text: 'You understand these products are for research use only' },
              { text: 'You agree to comply with all applicable laws' }
            ].map((item, index) => (
              <div 
                key={index}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '8px 12px',
                  background: index % 2 === 0 ? 'rgba(15, 46, 109, 0.2)' : 'var(--bg-secondary)',
                  borderRadius: '6px',
                  marginBottom: '4px'
                }}
              >
                <div 
                  style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    background: '#00E5FF',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}
                >
                  <CheckCircle size={12} style={{ color: '#ffffff' }} />
                </div>
                <span style={{ 
                  fontSize: '12px', 
                  color: '#FFFFFF',
                  fontWeight: '500'
                }}>
                  {item.text}
                </span>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', gap: '10px', marginBottom: '12px' }}>
            <button
              onClick={handleVerify}
              data-testid="age-verify-confirm"
              style={{
                flex: 1,
                background: 'linear-gradient(135deg, #00E5FF 0%, #0A1F44 100%)',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                padding: '12px 20px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                boxShadow: '0 4px 14px rgba(13, 92, 117, 0.3)'
              }}
              onMouseOver={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 20px rgba(13, 92, 117, 0.4)';
              }}
              onMouseOut={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 14px rgba(13, 92, 117, 0.3)';
              }}
            >
              {t('ageVerification.confirm')}
            </button>
            <button
              onClick={handleDecline}
              data-testid="age-verify-decline"
              style={{
                flex: 1,
                background: 'var(--bg-secondary)',
                color: '#C0C7D1',
                border: '2px solid #e0e0e0',
                borderRadius: '8px',
                padding: '12px 20px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => {
                e.target.style.borderColor = '#00E5FF';
                e.target.style.color = '#00E5FF';
              }}
              onMouseOut={(e) => {
                e.target.style.borderColor = '#e0e0e0';
                e.target.style.color = '#C0C7D1';
              }}
            >
              Exit
            </button>
          </div>

          <p style={{ 
            fontSize: '10px', 
            color: '#7A8599',
            textAlign: 'center',
            margin: 0,
            lineHeight: '1.6'
          }}>
            By clicking "I Confirm & Enter", you agree to our{' '}
            <a 
              href="/terms" 
              style={{ color: '#00E5FF', textDecoration: 'none', fontWeight: '500' }}
              onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
              onMouseOut={(e) => e.target.style.textDecoration = 'none'}
            >
              Terms of Service
            </a>{' '}
            and{' '}
            <a 
              href="/disclaimer" 
              style={{ color: '#00E5FF', textDecoration: 'none', fontWeight: '500' }}
              onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
              onMouseOut={(e) => e.target.style.textDecoration = 'none'}
            >
              Research Use Disclaimer
            </a>
          </p>
        </div>
      </div>
    </div>
    </>
  );
};

export default AgeVerification;
